import { Reducer } from "redux";
import { BlockMapper, QuoteProduct } from "@/Contracts/ProposalBuilder/Block";
import {
	BlocksActions,
	BlocksActionTypes
} from "@/ProposalBuilder/store/actions/Blocks";
import { EditorType } from "@/Contracts";

export interface BlockState {
	readonly isLoading: boolean;
	readonly blockMapper: BlockMapper;
	readonly currentActiveBlock: (string | null)[];
	readonly currentActiveSection: number | null;
	readonly loadingQuoteBlockIds: string[];
	readonly isQuoteBlockInDocumentChanged?: boolean;
	readonly inputFieldPopUpVisible: boolean;
	readonly backgroundColorForTextBlockVisible: boolean;
	readonly inputFieldSelector: string | null;
	readonly isProductConfigureDrawerVisible: boolean;
	readonly productData: QuoteProduct | null;
	readonly blockId: string;
	readonly sectionIndex: number;
	readonly isImportProductsToQuoteDrawerVisible: boolean;
	readonly importProductsToQuoteDrawerBlockId: string;
	readonly importProductsToQuoteDrawerSectionIndex: number;
	readonly editorType: EditorType | null;
	readonly readonlyProductDrawer: boolean;
	readonly headerFooterColumnId: string | null;
}

export const initialBlockState: BlockState = {
	isLoading: false,
	isQuoteBlockInDocumentChanged: false,
	blockMapper: {},
	currentActiveBlock: new Array<string | null>(),
	currentActiveSection: null,
	loadingQuoteBlockIds: [],
	inputFieldPopUpVisible: false,
	backgroundColorForTextBlockVisible: false,
	inputFieldSelector: null,
	isProductConfigureDrawerVisible: false,
	productData: null,
	sectionIndex: 0,
	blockId: "",
	isImportProductsToQuoteDrawerVisible: false,
	importProductsToQuoteDrawerBlockId: "",
	importProductsToQuoteDrawerSectionIndex: 0,
	editorType: null,
	readonlyProductDrawer: false,
	headerFooterColumnId: null
};

const BlocksReducer: Reducer<BlockState, BlocksActions> = (
	state: BlockState = initialBlockState,
	action: BlocksActions
): BlockState => {
	switch (action.type) {
		case BlocksActionTypes.SHOW_BLOCK_LOADER: {
			return {
				...state,
				isLoading: true
			};
		}
		case BlocksActionTypes.HIDE_BLOCK_LOADER: {
			return {
				...state,
				isLoading: false
			};
		}
		case BlocksActionTypes.GET_BLOCK_MAPPER_SUCCESSFUL: {
			return {
				...state,
				blockMapper: action.blockMapper
			};
		}
		case BlocksActionTypes.UPDATE_BLOCK_MAPPER_REQUESTED: {
			return state;
		}
		case BlocksActionTypes.UPDATE_BLOCK_MAPPER_SUCCESSFUL: {
			return {
				...state,
				blockMapper: {
					...state.blockMapper,
					...action.blockMapper
				}
			};
		}
		case BlocksActionTypes.UPDATE_BLOCK_FOCUS: {
			return {
				...state,
				currentActiveBlock: action.currentActiveBlock
			};
		}
		case BlocksActionTypes.UPDATE_SECTION_FOCUS: {
			return {
				...state,
				currentActiveSection: action.currentActiveSection
			};
		}
		case BlocksActionTypes.RESET_BLOCK_FOCUS: {
			return {
				...state,
				currentActiveBlock: new Array<string | null>(),
				currentActiveSection: null
			};
		}
		case BlocksActionTypes.CREATE_BLOCK_SUCCESSFUL: {
			return {
				...state,
				blockMapper: {
					...state.blockMapper,
					[action.blockData.id]: action.blockData
				}
			};
		}
		case BlocksActionTypes.UPDATE_BLOCK_SUCCESSFUL: {
			return {
				...state,
				isQuoteBlockInDocumentChanged: !!action.isQuoteBlock,
				blockMapper: {
					...state.blockMapper,
					[action.blockData.id]: action.blockData
				}
			};
		}
		case BlocksActionTypes.HIDE_BLOCK_SUCCESSFUL: {
			return {
				...state,
				blockMapper: {
					...state.blockMapper,
					[action.blockData.id]: action.blockData
				}
			};
		}
		case BlocksActionTypes.HIDE_BLOCK_REQUESTED: {
			return state;
		}
		case BlocksActionTypes.HIDE_BLOCK_FAILED: {
			return state;
		}
		case BlocksActionTypes.UNHIDE_BLOCK_SUCCESSFUL: {
			return {
				...state,
				blockMapper: {
					...state.blockMapper,
					[action.blockData.id]: action.blockData
				}
			};
		}
		case BlocksActionTypes.UNHIDE_BLOCK_REQUESTED: {
			return state;
		}
		case BlocksActionTypes.UNHIDE_BLOCK_FAILED: {
			return state;
		}
		case BlocksActionTypes.FETCH_SINGLE_BLOCK_SUCCESSFUL: {
			return {
				...state,
				blockMapper: {
					...state.blockMapper,
					[action.block.id]: action.block
				}
			};
		}
		case BlocksActionTypes.FETCH_SINGLE_BLOCK_REQUESTED:
		case BlocksActionTypes.FETCH_SINGLE_BLOCK_FAILED: {
			return state;
		}
		case BlocksActionTypes.EDIT_QUOTE_LABEL_SUCCESSFUL: {
			return {
				...state,
				blockMapper: {
					...state.blockMapper,
					[action.blockData.id]: action.blockData
				}
			};
		}
		case BlocksActionTypes.EDIT_QUOTE_LABEL_FAILED: {
			return state;
		}
		case BlocksActionTypes.EDIT_QUOTE_LABEL_REQUESTED: {
			return state;
		}
		case BlocksActionTypes.UPDATE_BLOCK_REQUESTED: {
			return state;
		}
		case BlocksActionTypes.UPDATE_BLOCK_FAILED: {
			return state;
		}
		case BlocksActionTypes.GET_BLOCK_MAPPER_REQUESTED: {
			return state;
		}
		case BlocksActionTypes.GET_BLOCK_MAPPER_FAILED: {
			return state;
		}
		case BlocksActionTypes.UPDATE_BLOCK_MAPPER_FAILED: {
			return state;
		}
		case BlocksActionTypes.CREATE_BLOCK_REQUESTED: {
			return state;
		}
		case BlocksActionTypes.CREATE_BLOCK_FAILED: {
			return state;
		}
		case BlocksActionTypes.DELETE_BLOCK_REQUESTED: {
			return state;
		}
		case BlocksActionTypes.DELETE_BLOCK_SUCCESSFUL: {
			return {
				...state,
				blockMapper: action.blockMapper
			};
		}
		case BlocksActionTypes.DELETE_BLOCK_FAILED: {
			return state;
		}
		case BlocksActionTypes.UPDATE_QUOTE_BLOCK_LOADER_IDS: {
			return { ...state, loadingQuoteBlockIds: action.quoteBlockIds };
		}
		case BlocksActionTypes.QUICKBOOKS_SYNC_TOOLTIP_SHOW: {
			return {
				...state,
				isQuoteBlockInDocumentChanged: true
			};
		}
		case BlocksActionTypes.QUICKBOOKS_SYNC_TOOLTIP_HIDE: {
			return {
				...state,
				isQuoteBlockInDocumentChanged: false
			};
		}
		case BlocksActionTypes.SHOW_INPUT_FIELD_POP_UP: {
			return {
				...state,
				inputFieldPopUpVisible: true
			};
		}
		case BlocksActionTypes.HIDE_INPUT_FIELD_POP_UP: {
			return {
				...state,
				inputFieldPopUpVisible: false
			};
		}
		case BlocksActionTypes.SHOW_BACKGROUND_COLOR_PICKER: {
			return {
				...state,
				backgroundColorForTextBlockVisible: true
			};
		}
		case BlocksActionTypes.HIDE_BACKGROUND_COLOR_PICKER: {
			return {
				...state,
				backgroundColorForTextBlockVisible: false
			};
		}
		case BlocksActionTypes.SHOW_PRODUCT_CONFIGURE_DRAWER: {
			return {
				...state,
				isProductConfigureDrawerVisible: true,
				blockId: action.blockId,
				sectionIndex: action.sectionIndex,
				productData: action.productData,
				readonlyProductDrawer: action.readonly ?? false
			};
		}
		case BlocksActionTypes.HIDE_PRODUCT_CONFIGURE_DRAWER: {
			return {
				...state,
				isProductConfigureDrawerVisible: false
			};
		}
		case BlocksActionTypes.SHOW_IMPORT_PRODUCTS_TO_QUOTE_DRAWER: {
			return {
				...state,
				isImportProductsToQuoteDrawerVisible: true,
				importProductsToQuoteDrawerBlockId: action.blockId,
				importProductsToQuoteDrawerSectionIndex: action.sectionIndex
			};
		}
		case BlocksActionTypes.HIDE_IMPORT_PRODUCTS_TO_QUOTE_DRAWER: {
			return {
				...state,
				isImportProductsToQuoteDrawerVisible: false,
				importProductsToQuoteDrawerBlockId: "",
				importProductsToQuoteDrawerSectionIndex: 0
			};
		}
		case BlocksActionTypes.UPDATE_EDITOR_TYPE: {
			return {
				...state,
				editorType: action.editorType
			};
		}
		case BlocksActionTypes.UPDATE_HEADER_FOOTER_COLUMN_ID: {
			return {
				...state,
				headerFooterColumnId: action.headerFooterColumnId
			};
		}
		default: {
			return state;
		}
	}
};

export default BlocksReducer;
